import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { SequenceBuilder } from './sequence-builder'
import { useUpdateSequence } from './use-campaigns'
import type { SequenceConfig, SequenceStep, StyleSamplesMode } from './types'

type EditSequenceDialogProps = {
  readonly campaignId: string
  readonly currentSequence: SequenceConfig
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function EditSequenceDialog({
  campaignId,
  currentSequence,
  open,
  onOpenChange,
}: EditSequenceDialogProps) {
  const [steps, setSteps] = useState<readonly SequenceStep[]>(
    currentSequence.steps,
  )
  const [styleSamplesMode, setStyleSamplesMode] = useState<StyleSamplesMode>(
    currentSequence.style_samples_mode ?? 'global',
  )
  const [styleSamplesGlobal, setStyleSamplesGlobal] = useState<readonly string[]>(
    currentSequence.style_samples_global ?? [],
  )

  const updateSequence = useUpdateSequence()

  function handleSave() {
    const updatedSequence: SequenceConfig = {
      ...currentSequence,
      steps,
      style_samples_mode: styleSamplesMode,
      style_samples_global: styleSamplesGlobal.filter((s) => s.trim().length > 0),
    }

    updateSequence.mutate(
      { campaignId, sequence: updatedSequence },
      {
        onSuccess: () => onOpenChange(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Outreach Sequence</DialogTitle>
        </DialogHeader>

        <div className="py-4 max-h-[60vh] overflow-y-auto">
          <SequenceBuilder
            steps={steps}
            onChange={setSteps}
            styleSamplesMode={styleSamplesMode}
            onStyleSamplesModeChange={setStyleSamplesMode}
            styleSamplesGlobal={styleSamplesGlobal}
            onStyleSamplesGlobalChange={setStyleSamplesGlobal}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateSequence.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateSequence.isPending || steps.length === 0}
          >
            {updateSequence.isPending ? 'Saving...' : 'Save Sequence'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
